import { useId, useLayoutEffect, useRef, useState } from 'react'
import { fontFamilyByVariant, resolveTextColorConfig, splitTextIntoLines, TextBounds, TextHeadlineColorPreset } from './textConfig'

export type TextHeadlineProps = {
    children: string
    className?: string
    fontSize?: number
    maxCharsPerLine?: number
    fontFamily?: 'nunito' | 'grandstander'
    fontWeight?: string | number
    italic?: boolean
    leading?: number
    tracking?: string | number
    textColor?: string
    colorPreset?: TextHeadlineColorPreset
    outlineColor?: string
    outlineFillColor?: string
    outlineDropShadowColor?: string
    dropShadowColor?: string
    curveAmount?: number
}

export default function TextHeadline({
    children,
    className,
    fontSize = 48,
    maxCharsPerLine = 22,
    fontFamily = 'grandstander',
    fontWeight = 700,
    italic = true,
    leading = 0.875,
    tracking = 0,
    textColor = '#FFB001',
    colorPreset,
    outlineColor,
    outlineFillColor = '#FFFFFF',
    outlineDropShadowColor = '#FFFFFF',
    dropShadowColor,
    curveAmount = 0,
}: TextHeadlineProps) {
    const baseId = useId().replace(/:/g, '')
    const outlineShadowId = `${baseId}-text-headline-outline-shadow`
    const shadowId = `${baseId}-text-headline-shadow`
    const textRef = useRef<SVGGElement>(null)
    const [textBounds, setTextBounds] = useState<TextBounds | null>(null)
    const lines = splitTextIntoLines(children, maxCharsPerLine)
    const lineHeight = fontSize * leading
    const outlineStrokeWidth = Math.max(7, fontSize / 3)
    const headlineStrokeWidth = Math.min(8, Math.max(1.5, fontSize / 14))
    const viewBoxHeight = lines.length * lineHeight + outlineStrokeWidth * 3 + curveAmount
    const startY = outlineStrokeWidth + fontSize
    const boundsPadding = outlineStrokeWidth * 3
    const horizontalSafetyPadding = Math.max(outlineStrokeWidth * 4, fontSize * 0.75)
    const primaryFontFamily = fontFamily === 'grandstander' ? 'Grandstander' : 'Nunito'
    const longestLineLength = Math.max(...lines.map((line) => line.length), 1)
    const estimatedWidth = longestLineLength * fontSize * 0.75 + boundsPadding * 2
    const viewBoxX = textBounds?.x ?? -estimatedWidth / 2
    const viewBoxWidth = textBounds?.width ?? estimatedWidth
    const lineKey = lines.join('\n')
    const presetConfig = resolveTextColorConfig({ colorPreset, textColor })
    const resolvedTextColor = presetConfig.textColor
    const resolvedOutlineColor = outlineColor ?? presetConfig.outlineColor
    const resolvedDropShadowColor = dropShadowColor ?? presetConfig.dropShadowColor
    const outlineShadowOffsetY = Math.max(1, Math.round(fontSize / 20))
    const dropShadowOffsetY = Math.max(1, Math.round(fontSize / 24))

    useLayoutEffect(() => {
        let isCancelled = false
        const timeoutIds: number[] = []

        const measureText = () => {
            const textElement = textRef.current

            if (!textElement) return

            const box = textElement.getBBox()
            const nextBounds = {
                x: box.x - boundsPadding - horizontalSafetyPadding,
                width: box.width + boundsPadding * 2 + horizontalSafetyPadding * 2,
            }

            if (isCancelled) return

            setTextBounds((currentBounds) => {
                if (
                    currentBounds
                    && Math.abs(currentBounds.x - nextBounds.x) < 0.5
                    && Math.abs(currentBounds.width - nextBounds.width) < 0.5
                ) {
                    return currentBounds
                }

                return nextBounds
            })
        }

        const scheduleMeasureBurst = () => {
            const delays = [0, 50, 150, 300]

            for (const delay of delays) {
                const timeoutId = window.setTimeout(() => {
                    window.requestAnimationFrame(() => {
                        if (!isCancelled) measureText()
                    })
                }, delay)

                timeoutIds.push(timeoutId)
            }
        }

        scheduleMeasureBurst()

        const fontFace = `${italic ? 'italic' : 'normal'} ${fontWeight} ${fontSize}px "${primaryFontFamily}"`
        const handleFontLoadingDone = () => {
            if (!isCancelled) scheduleMeasureBurst()
        }

        if (document.fonts) {
            void Promise.all([
                document.fonts.ready,
                document.fonts.load(fontFace, children),
            ]).then(() => {
                if (!isCancelled) scheduleMeasureBurst()
            })

            document.fonts.addEventListener('loadingdone', handleFontLoadingDone)
        }

        return () => {
            isCancelled = true

            for (const timeoutId of timeoutIds) {
                window.clearTimeout(timeoutId)
            }

            document.fonts?.removeEventListener('loadingdone', handleFontLoadingDone)
        }
    }, [boundsPadding, children, fontSize, fontWeight, horizontalSafetyPadding, italic, lineKey, primaryFontFamily, tracking])

    return (
        <svg
            role='img'
            aria-label={children}
            viewBox={`${viewBoxX} 0 ${viewBoxWidth} ${viewBoxHeight}`}
            className={className}
            style={{
                display: 'block',
                width: `${viewBoxWidth}px`,
                height: `${viewBoxHeight}px`,
                overflow: 'visible',
            }}
        >
            <defs>
                <filter
                    id={outlineShadowId}
                    x='-20%'
                    y='-25%'
                    width='140%'
                    height='160%'
                    colorInterpolationFilters='sRGB'
                >
                    <feOffset in='SourceAlpha' dx='0' dy={String(outlineShadowOffsetY)} result='outlineDropOffset' />
                    <feFlood floodColor={outlineDropShadowColor} floodOpacity='1' result='outlineDropColor' />
                    <feComposite in='outlineDropColor' in2='outlineDropOffset' operator='in' result='outlineDropShadow' />
                    <feMerge>
                        <feMergeNode in='outlineDropShadow' />
                        <feMergeNode in='SourceGraphic' />
                    </feMerge>
                </filter>

                <filter
                    id={shadowId}
                    x='-20%'
                    y='-25%'
                    width='140%'
                    height='160%'
                    colorInterpolationFilters='sRGB'
                >
                    <feOffset in='SourceAlpha' dx='0' dy={String(dropShadowOffsetY)} result='dropOffset' />
                    <feFlood floodColor={resolvedDropShadowColor} floodOpacity='1' result='dropColor' />
                    <feComposite in='dropColor' in2='dropOffset' operator='in' result='dropShadow' />
                    <feMerge>
                        <feMergeNode in='dropShadow' />
                        <feMergeNode in='SourceGraphic' />
                    </feMerge>
                </filter>

                {lines.map((_, index) => {
                    const lineY = startY + index * lineHeight
                    const hw = viewBoxWidth / 2 + boundsPadding
                    return (
                        <path
                            key={`curve-path-${index}`}
                            id={`${baseId}-curve-${index}`}
                            d={
                                curveAmount > 0
                                    ? `M ${-hw},${lineY} Q 0,${lineY - 2 * curveAmount} ${hw},${lineY}`
                                    : `M ${-hw},${lineY} H ${hw}`
                            }
                            fill='none'
                        />
                    )
                })}
            </defs>

            <g ref={textRef}>
                <g filter={`url(#${outlineShadowId})`}>
                    {lines.map((line, index) => (
                        <text
                            key={`outline-${line}-${index}`}
                            textAnchor='middle'
                            dominantBaseline='alphabetic'
                            fontFamily={fontFamilyByVariant[fontFamily]}
                            fontSize={fontSize}
                            fontWeight={fontWeight}
                            fontStyle={italic ? 'italic' : undefined}
                            letterSpacing={tracking}
                            fill={outlineFillColor}
                            stroke={outlineFillColor}
                            strokeWidth={outlineStrokeWidth}
                            strokeLinejoin='round'
                            paintOrder='stroke fill'
                            className='uppercase'
                        >
                            <textPath
                                href={`#${baseId}-curve-${index}`}
                                startOffset='50%'
                            >
                                {line}
                            </textPath>
                        </text>
                    ))}
                </g>

                <g filter={`url(#${shadowId})`}>
                    {lines.map((line, index) => (
                        <text
                            key={`headline-${line}-${index}`}
                            textAnchor='middle'
                            dominantBaseline='alphabetic'
                            fontFamily={fontFamilyByVariant[fontFamily]}
                            fontSize={fontSize}
                            fontWeight={fontWeight}
                            fontStyle={italic ? 'italic' : undefined}
                            letterSpacing={tracking}
                            fill={resolvedTextColor}
                            stroke={resolvedOutlineColor}
                            strokeWidth={headlineStrokeWidth}
                            strokeLinejoin='round'
                            paintOrder='stroke fill'
                            className='uppercase'
                        >
                            <textPath
                                href={`#${baseId}-curve-${index}`}
                                startOffset='50%'
                            >
                                {line}
                            </textPath>
                        </text>
                    ))}
                </g>
            </g>
        </svg>
    )
}
